import { CommandsBase } from './base';
import { Distribution } from 'src/api';

export namespace Boards {
  export namespace Types {
    export type Board = {
      id: number;
      name: string;
      enabled: boolean;
      type: string;
      lastRun: string;
    };

    export interface BoardEnlated extends Board {
      isLate: boolean;
      lastRunFormatted: string;
      runAgo: string;
    }

    export type BoardSettings = {
      id: number;
      name: string;
      isIncremental: boolean;
      integrationType: string;
      lastRun: string;
      feedPath: string;
    };

    export type BoardSchedulingInterval = {
      value: number;
      type: 'hour' | 'mins';
    };

    export type BoardDeliveryRuntimeOptions =
      | string[]
      | 'realtime'
      | BoardSchedulingInterval;

    export type BoardDeliveryRuntimeType =
      | 'realtime'
      | 'interval'
      | 'scheduled';

    export interface BoardDeliveryRuntime {
      type: BoardDeliveryRuntimeType;
      options: BoardDeliveryRuntimeOptions;
    }

    export interface BoardDeliveryProps {
      boardId: number;
      enabled?: boolean;
      runtime?: BoardDeliveryRuntime;
    }

    export interface BoardDeliveryResponse extends BoardDeliveryProps {
      message?: string;
    }

    export interface BoardActionProps {
      boardId: number;
    }

    export interface BoardListProps {
      limit?: number;
      offset?: number;
      includeLocalOutreach?: boolean;
    }

    export interface BoardFeedFile {
      url: string;
      name: string;
      size: string;
      timestamp: string;
      date: Date;
    }

    export type BoardFeedSampleResponse = string[];
    export type BoardFeedFilesResponse = BoardFeedFile[];
  }

  const getRelativeRunAgo = (duration: number) => {
    // Hours, minutes and seconds
    const hrs = ~~(duration / 3600);
    const mins = ~~((duration % 3600) / 60);
    const secs = ~~duration % 60;

    // Output like "1:01" or "4:03:59" or "123:03:59"
    let ret = '';

    if (hrs > 0) {
      ret += `${hrs}:${mins < 10 ? '0' : ''}`;
    }

    ret += `${mins}:${secs < 10 ? '0' : ''}`;
    ret += `${secs}`;
    return ret;
  };

  const ONE_DAY_TIME = 24 * 3600 * 1000;

  export const enlateBoard = (
    board: Boards.Types.Board
  ): Types.BoardEnlated => {
    const runAgoTime = Date.now() - Date.parse(board.lastRun); // todo: add lastRunTime on server side
    const isLate = runAgoTime >= ONE_DAY_TIME; // todo: add `expired` flag on server?
    return {
      ...board,
      lastRunFormatted: board.lastRun?.replace('T', ' ').substring(0, 19),
      runAgo: board.lastRun ? getRelativeRunAgo(runAgoTime / 1000) : '',
      isLate,
    };
  };

  export const SCHEDULING_TYPES = {
    SCHEDULED: {
      label: 'Scheduled',
      value: 'scheduled',
    },
    REALTIME: {
      label: 'Realtime',
      value: 'realtime',
    },
    INTERVAL: {
      label: 'Interval',
      value: 'interval',
    },
  };

  export const INTERVAL_OPTIONS = {
    HOUR: {
      label: 'Hours',
      value: 'hour',
    },
    MINS: {
      label: 'Minutes',
      value: 'mins',
    },
  };

  export const SERVICE = 'boards';

  export const ACTIONS = {
    list: 'list',
    settings: 'settings',
    getFeedSample: 'getFeedSample',
    getFeedFiles: 'getFeedFiles',
    getScheduling: 'getScheduling',
    setScheduling: 'setScheduling',
    // posting service actions
    createScheduling: 'createScheduling',
    scheduling: 'scheduling',
    // statistics: distribution.getStatistics,
    // executions: stepFunctions.list,
  };

  export const SKIP_EVENTS = [ACTIONS.scheduling, ACTIONS.settings];

  export class Commands extends CommandsBase {
    // getBoardList() {
    //   return this.client.execute<Record<string, unknown>, Types.Board[]>(
    //     this.command('list'),
    //     {}
    //   );
    // }

    getBoards(data: Types.BoardListProps) {
      return this.client.execute<Types.BoardListProps, Types.Board[]>(
        this.command(ACTIONS.list),
        data
      );
    }

    async loadAllBoards({
      includeLocalOutreach = false,
      batchCb,
    }: {
      includeLocalOutreach?: boolean;
      batchCb?: (boards: Types.Board[]) => void;
    }) {
      const BATCH_SIZE = 300;
      let lastFetch = 0;
      let fetched = 0;

      do {
        const data = await this.getBoards({
          limit: BATCH_SIZE,
          offset: fetched,
          includeLocalOutreach,
        });
        lastFetch = data.length;
        fetched += lastFetch;

        batchCb && batchCb(data);
      } while (lastFetch === BATCH_SIZE);

      return fetched;
    }

    getBoardSettings(data: Types.BoardActionProps) {
      return this.client.execute<Types.BoardActionProps, Types.BoardSettings>(
        this.command(ACTIONS.settings),
        data
      );
    }

    getBoardDeliveryProps(data: Types.BoardActionProps) {
      return this.client.execute<
        Types.BoardActionProps,
        Types.BoardDeliveryResponse
      >(this.command(ACTIONS.getScheduling), data);
    }

    setBoardDeliveryProps(data: Types.BoardDeliveryProps) {
      return this.client.execute<
        Types.BoardDeliveryProps,
        Types.BoardDeliveryResponse
      >(this.command(ACTIONS.setScheduling), data);
    }

    createBoardDelivery(data: Types.BoardDeliveryProps) {
      return this.client.execute<
        Types.BoardDeliveryProps,
        Types.BoardDeliveryResponse
      >(this.command(ACTIONS.createScheduling), data);
    }

    getBoardFeedSample(data: Types.BoardActionProps) {
      return this.client.execute<
        Types.BoardActionProps,
        Types.BoardFeedSampleResponse
      >(this.command(ACTIONS.getFeedSample, Distribution.SERVICE), data);
    }

    async getBoardFeedFiles(data: Types.BoardActionProps) {
      return this.client.execute<
        Types.BoardActionProps,
        Types.BoardFeedFilesResponse
      >(this.command(ACTIONS.getFeedFiles, Distribution.SERVICE), data);
    }
  }
}
